import type { Question, QuestionMaterial } from './questions';

/** Authored simulation data, not reported statistics or exam quotations. */
export const ANALYSIS_MATERIALS: QuestionMaterial[] = [
  {
    id: 'revenue', title: '资料一 · 装备企业季度经营',
    description: '某装备企业各季度营业收入与销量如下。收入单位为万元，销量单位为台。',
    columns: ['季度', '2023年收入', '2024年收入', '2024年销量'],
    rows: [['一季度', '12480', '14976', '1920'], ['二季度', '15600', '17940', '2300'],
      ['三季度', '16800', '20160', '2520'], ['四季度', '19120', '22944', '2860']],
    note: '模拟练习数据；营业收入可按同一统计口径加总。'
  },
  {
    id: 'industry', title: '资料二 · 工业行业产值',
    description: '某地区2024年四个行业产值及同比增长率如下。产值单位为亿元；同比均相对2023年。',
    columns: ['行业', '2024年产值', '同比增长率'],
    rows: [['电子', '1680', '12%'], ['装备', '1560', '20%'], ['医药', '990', '10%'], ['新能源', '1260', '35%']],
    note: '模拟练习数据；“四行业合计”仅指表中四个行业。'
  },
  {
    id: 'trade', title: '资料三 · 进出口统计',
    description: '某地区进出口金额如下，单位为亿元。进出口总额＝出口额＋进口额；顺差＝出口额－进口额。',
    columns: ['年份', '进出口总额', '出口额', '进口额'],
    rows: [['2022', '3000', '1800', '1200'], ['2023', '3600', '2100', '1500'], ['2024', '4320', '2604', '1716']],
    note: '模拟练习数据；各年统计口径一致。'
  },
  {
    id: 'quality', title: '资料四 · 生产线质量与效率',
    description: '四条生产线2024年数据如下。合格品数量单位为万件，人数单位为人；合格率＝合格品数量÷总产量。',
    columns: ['生产线', '合格品数量', '合格率', '平均人数'],
    rows: [['甲', '54', '90%', '150'], ['乙', '76', '95%', '200'], ['丙', '49.5', '99%', '180'], ['丁', '57', '95%', '190']],
    note: '模拟练习数据；合计人均指标按总数量÷总人数计算。'
  },
  {
    id: 'efficiency', title: '资料五 · 产值、资源与投入',
    description: '某企业两年的经营指标如下，金额、资源量均按同一口径统计。',
    columns: ['指标（单位）', '2023年', '2024年'],
    rows: [['产值（亿元）', '16', '20'], ['用电（万千瓦时）', '4800', '5400'], ['用水（万吨）', '600', '660'],
      ['研发投入（亿元）', '0.80', '1.10'], ['平均人数（人）', '800', '880']],
    note: '模拟练习数据；研发投入强度＝研发投入÷产值。'
  }
];

type Row = [number, string, string, string[], number, string];
const rows: Row[] = [
  [0, '同比增长率', '2024年二季度营业收入同比增长多少？', ['12.0%', '15.0%', '17.5%', '20.0%'], 1,
    '同比增长率＝（现期－基期）÷基期。计算：（17940－15600）÷15600＝15%。分母应取2023年同期收入。'],
  [0, '综合增长率', '2024年全年营业收入同比增长约多少？（保留1位小数）', ['17.5%', '18.0%', '18.8%', '20.0%'], 2,
    '先加总：2023年收入64000万元，2024年76020万元。增长率＝12020÷64000≈18.8%。不能直接把四个季度增长率做算术平均。'],
  [0, '现期比重', '2024年三季度收入占全年收入的比重约为多少？（保留1位小数）', ['23.5%', '25.0%', '26.5%', '28.0%'], 2,
    '全年收入＝14976＋17940＋20160＋22944＝76020万元。比重＝20160÷76020≈26.5%。'],
  [0, '平均数与单位', '按营业收入÷销量计算，2024年四季度平均单台收入约多少万元？（保留2位小数）', ['0.80万元/台', '8.02万元/台', '8.80万元/台', '80.22万元/台'], 1,
    '平均单台收入＝22944万元÷2860台≈8.02万元/台。这里收入已是万元，无需再乘或除10000。'],
  [0, '同比增长量', '2024年三季度营业收入同比增加多少万元？', ['2340万元', '2992万元', '3360万元', '3824万元'], 2,
    '增长量＝现期－基期＝20160－16800＝3360万元。增长量带金额单位，不能与增长率混用。'],
  [0, '跨期比较', '2024年下半年收入比上半年高约多少？（保留1位小数）', ['23.6%', '28.0%', '31.0%', '34.1%'], 2,
    '上半年32916万元，下半年43104万元。相对上半年的增幅＝（43104－32916）÷32916≈30.9515%，保留1位小数为31.0%。比较基准是上半年，不是下半年。'],
  [1, '基期量', '2023年装备行业产值为多少亿元？', ['1248亿元', '1300亿元', '1350亿元', '1872亿元'], 1,
    '基期量＝现期量÷（1＋增长率）＝1560÷1.20＝1300亿元。直接用1560×80%不是正确的逆增长计算。'],
  [1, '增长量速算', '2024年电子行业产值同比增加多少亿元？', ['180亿元', '187亿元', '202亿元', '210亿元'], 0,
    '增长量＝现期量×增长率÷（1＋增长率）＝1680×12%÷1.12＝180亿元；也可先算基期1500，再作差。'],
  [1, '增长量速算', '2024年新能源行业产值同比增加约多少亿元？（取整数）', ['233亿元', '294亿元', '327亿元', '441亿元'], 2,
    '增长量＝1260×35%÷1.35≈326.67亿元，取整为327亿元。1260×35%把现期量错当成了基期量。'],
  [1, '合计增长率', '2024年四行业合计产值同比增长约多少？（保留1位小数）', ['18.5%', '19.3%', '20.0%', '22.0%'], 0,
    '现期合计5490亿元；基期合计＝1680÷1.12＋1560÷1.20＋990÷1.10＋1260÷1.35≈4633.33亿元。合计增长率≈18.5%，不能平均四个行业的增长率。'],
  [1, '两期比重变化', '装备行业产值占四行业合计的比重，2024年比2023年约提高多少个百分点？（保留2位小数）', ['0.15个百分点', '0.36个百分点', '1.28个百分点', '1.51个百分点'], 1,
    '2024年比重＝1560÷5490≈28.415%；2023年合计约4633.33亿元，装备基期1300亿元，比重约28.058%。两期相减约提高0.36个百分点；“百分点”是比重的直接差。'],
  [1, '混合增长率', '电子和医药两行业合计产值，2024年同比增长多少？（保留2位小数）', ['10.50%', '11.00%', '11.25%', '11.50%'], 2,
    '电子基期1500亿元，医药基期900亿元，合计2400；现期1680＋990＝2670。合计增长率＝270÷2400＝11.25%，不是（12%＋10%）÷2。'],
  [2, '增长量', '2024年出口额比2023年增加多少亿元？', ['456亿元', '504亿元', '600亿元', '720亿元'], 1,
    '出口增长量＝2604－2100＝504亿元。进出口总额增加720亿元，不能将总额增长量当作出口增长量。'],
  [2, '差额计算', '2024年贸易顺差为多少亿元？', ['600亿元', '720亿元', '888亿元', '984亿元'], 2,
    '顺差＝出口额－进口额＝2604－1716＝888亿元。'],
  [2, '差额增长率', '2024年贸易顺差同比增长多少？', ['28.0%', '32.4%', '40.0%', '48.0%'], 3,
    '2023年顺差＝2100－1500＝600亿元，2024年888亿元。顺差同比增长＝（888－600）÷600＝48%。不能用出口增长率代替顺差增长率。'],
  [2, '增长贡献率', '2024年出口增长量占进出口总额增长量的比重为多少？', ['60.3%', '65.0%', '70.0%', '75.6%'], 2,
    '出口增长量504亿元，总额增长量＝4320－3600＝720亿元。贡献率＝504÷720＝70%。它与出口额占总额的比重含义不同。'],
  [2, '两期比重变化', '出口额占进出口总额的比重，2024年比2023年约提高多少个百分点？（保留2位小数）', ['1.20个百分点', '1.94个百分点', '2.40个百分点', '3.33个百分点'], 1,
    '2024年比重＝2604÷4320≈60.2778%，2023年＝2100÷3600≈58.3333%，相减约提高1.94个百分点。'],
  [2, '年均增长率', '2022年至2024年，进出口总额年均增长率为多少？', ['18.0%', '20.0%', '22.0%', '44.0%'], 1,
    '2022到2024包含两个增长周期。年均增长率＝（4320÷3000）的平方根－1＝20%；累计增长率44%不能除以2得到年均增长率。'],
  [3, '反推总量', '四条生产线总产量合计为多少万件？', ['237万件', '245万件', '250万件', '260万件'], 2,
    '各线总产量＝合格品÷合格率：甲60、乙80、丙50、丁60万件，合计250万件。'],
  [3, '加权平均', '四条生产线合计合格率为多少？', ['94.75%', '94.60%', '95.00%', '95.50%'], 1,
    '合格品合计＝54＋76＋49.5＋57＝236.5万件，总产量250万件，合计合格率＝236.5÷250＝94.6%。不是四个合格率的简单平均94.75%。'],
  [3, '人均量与单位', '四条生产线合计人均合格品产量约为多少件？（取整数）', ['2750件/人', '3000件/人', '3285件/人', '3472件/人'], 2,
    '总合格品236.5万件＝2365000件，总人数150＋200＋180＋190＝720人。合计人均＝2365000÷720≈3285件/人。'],
  [3, '平均数比较', '哪条生产线人均合格品产量最高？', ['甲', '乙', '丙', '丁'], 1,
    '甲＝540000÷150＝3600；乙＝760000÷200＝3800；丙＝495000÷180＝2750；丁＝570000÷190＝3000，单位均为件/人。乙最高。'],
  [3, '现期比重', '乙线合格品数量占四条线合计的比重约为多少？（保留1位小数）', ['28.0%', '30.4%', '32.1%', '33.8%'], 2,
    '合格品合计236.5万件，乙线76万件。比重＝76÷236.5≈32.1%；76÷250计算的是乙线合格品占全部总产量的比重，口径不同。'],
  [3, '百分点与增量', '若甲线总产量不变，合格率从90%提高到95%，合格品将增加多少万件？', ['2.7万件', '3.0万件', '5.0万件', '5.4万件'], 1,
    '甲线总产量＝54÷90%＝60万件。合格率提高5个百分点，合格品增加＝60×（95%－90%）＝3万件；不能直接用54×5%。'],
  [4, '平均数增长率', '2024年单位产值用电量比2023年下降多少？', ['8.0%', '10.0%', '12.5%', '20.0%'], 1,
    '2023年单位产值用电＝4800÷16＝300；2024年＝5400÷20＝270，单位均为万千瓦时/亿元。下降＝（300－270）÷300＝10%。也可算1－1.125÷1.25。'],
  [4, '强度与百分点', '2024年研发投入强度比2023年提高多少个百分点？', ['0.25个百分点', '0.50个百分点', '5.00个百分点', '10.00个百分点'], 1,
    '2023年投入强度＝0.80÷16＝5%；2024年＝1.10÷20＝5.5%。直接相减提高0.5个百分点；相对提高10%是另一个口径。'],
  [4, '人均增长率', '2024年人均产值同比增长约多少？（保留1位小数）', ['10.0%', '12.5%', '13.6%', '15.0%'], 2,
    '产值同比增长25%，人数增长10%。人均增长率＝（1＋25%）÷（1＋10%）－1≈13.6%；不能直接用25%－10%。'],
  [4, '单耗增长率', '2024年单位产值用水量同比下降多少？', ['10.0%', '12.0%', '15.0%', '20.0%'], 1,
    '单位产值用水分别为600÷16＝37.5、660÷20＝33，单位均为万吨/亿元。下降幅度＝1－33÷37.5＝12%。'],
  [4, '同比增长率', '2024年研发投入同比增长多少？', ['25.0%', '30.0%', '37.5%', '50.0%'], 2,
    '增长率＝（1.10－0.80）÷0.80＝37.5%。注意这里问研发投入金额的增幅，不是研发投入强度的变化。'],
  [4, '预测与比值变化', '若2025年产值较2024年增长10%、用电量增长5%，单位产值用电量将下降约多少？（保留2位小数）', ['4.55%', '5.00%', '5.26%', '9.09%'], 0,
    '单位产值用电量的增长因子＝用电增长因子÷产值增长因子＝1.05÷1.10。下降幅度＝1－1.05÷1.10≈4.55%；增长率之差5个百分点只是近似，不能作为本题精确结果。']
];

const answerPositions = [2, 3, 0, 1, 3, 1, 0, 0, 2, 1, 2, 3, 3, 0, 1, 2, 1, 2, 3, 0, 0, 1, 3, 2, 3, 2, 1, 0, 0, 1];
export const ANALYSIS_QUESTIONS: Question[] = rows.map(([material, topic, stem, options, answer, explanation], index) => {
  const choices = [...options];
  const correct = choices.splice(answer, 1)[0];
  const position = answerPositions[index];
  choices.splice(position, 0, correct);
  return { id: 'analysis-' + (index + 1), category: 'math', material: ANALYSIS_MATERIALS[material], topic,
    stem, options: choices, answer: position, explanation };
});
